import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { formatCurrency } from "../../../utils";
import { INVOICE_STATUS_DONUT_CONFIG } from "../../../constants/constants";

export type InvoiceStatusDonutProps = {
  paymentReceived: number;
  sendToCustomer: number;
  inProgress: number;
  pending: number;
  pastDueInvoicesCount: number;
  pastDueInvoicesAmount: number;
};

export const InvoiceStatusDonut = ({
  paymentReceived,
  sendToCustomer,
  inProgress,
  pending,
  pastDueInvoicesCount,
  pastDueInvoicesAmount,
}: InvoiceStatusDonutProps) => {
  const data = [
    {
      ...INVOICE_STATUS_DONUT_CONFIG[0],
      id: 0,
      value: paymentReceived,
    },
    {
      ...INVOICE_STATUS_DONUT_CONFIG[1],
      id: 1,
      value: sendToCustomer,
    },
    {
      ...INVOICE_STATUS_DONUT_CONFIG[2],
      id: 2,
      value: inProgress,
    },
    {
      ...INVOICE_STATUS_DONUT_CONFIG[3],
      id: 3,
      value: pending,
    },
  ];

  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <Box
      sx={{
        height: { xs: "auto", sm: 525 },
        minHeight: { xs: 0, sm: 525 },
        p: { xs: 2, sm: 3 },
        borderRadius: "18px",
        backgroundColor: "#fff",
        boxShadow: "0px 2px 12px rgba(0,0,0,.08)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 700,
          color: "#0F172A",
        }}
      >
        Invoice Status
      </Typography>

      <Typography
        sx={{
          color: "#94A3B8",
          fontSize: 14,
          mb: 1,
        }}
      >
        Invoice amounts for the selected period
      </Typography>

      <Box
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          minWidth: 0,
        }}
      >
        <PieChart
          width={260}
          height={200}
          margin={{
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
          }}
          series={[
            {
              innerRadius: 60,
              outerRadius: 95,
              data,
              valueFormatter: (item) => formatCurrency(item.value),
            },
          ]}
          slotProps={{
            legend: {
              hidden: true,
            },
          }}
        />

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: 110,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              color: "#94A3B8",
            }}
          >
            Total
          </Typography>

          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0F172A",
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            {formatCurrency(total)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: "auto" }}>
        {data.map((item) => {
          const percent =
            total > 0 ? Math.round((item.value / total) * 100) : 0;

          return (
            <StatusRow
              key={item.id}
              color={item.color}
              background={`${item.color}15`}
              label={item.label}
              percent={percent}
              amount={item.value}
            />
          );
        })}

        <StatusRow
          color="#D04E44"
          background="#D04E4415"
          label="Past Due"
          count={pastDueInvoicesCount}
          amount={pastDueInvoicesAmount}
        />
      </Box>
    </Box>
  );
};

type StatusRowProps = {
  color: string;
  background: string;
  label: string;
  percent?: number;
  amount: number;
  count?: number;
};
const StatusRow = ({
  color,
  background,
  label,
  percent,
  amount,
  count,
}: StatusRowProps) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      backgroundColor: background,
      borderRadius: "10px",
      px: 1.5,
      py: 0.75,
      mb: 0.75,
      minHeight: 40,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
        }}
      />

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: label === "Past Due" ? "#D04E44" : "#0F172A",
            overflowWrap: "anywhere",
          }}
        >
          {label}
        </Typography>

        {count !== undefined && (
          <Typography
            sx={{
              fontSize: 12,
              color: "#94A3B8",
              lineHeight: 1.2,
            }}
          >
            {count} invoices
          </Typography>
        )}
      </Box>
    </Box>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexShrink: 0,
      }}
    >
      {percent !== undefined && (
        <Typography
          sx={{
            color: "#94A3B8",
            fontSize: 13,
          }}
        >
          {percent}%
        </Typography>
      )}

      <Typography
        sx={{
          fontWeight: 700,
        }}
      >
        {formatCurrency(amount)}
      </Typography>
    </Box>
  </Box>
);
